import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router';

import {Button, Icon, IconButton, Text, WithTooltip} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import type {IconName} from '@fortawesome/fontawesome-common-types';
import {NewClassroomModal} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import NewFolderModal from 'components/PracticeView/FoldersModal/NewFolderModal/NewFolderModal.react';

import {useTeacherClassroomActions} from '../hooks/useTeacherClassrooms';
import {useTeacherDashboardStore} from '../useTeacherDashboardStore';

import {ClassSelector} from './ClassSelector';
import {CreateNewMenu} from './CreateNewMenu';
import type {NavScope} from './teacherNavScope';
import {getNavScope} from './teacherNavScope';
import {TOOLTIP_PROPS} from './config';

const HIDDEN_SCROLLBAR_CLASSES =
  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

interface SidemenuNavItem {
  icon: IconProp;
  key: string;
  label: string;
  route: string;
}

const HOME_ITEM: SidemenuNavItem = {icon: 'home', key: 'home', label: 'Home', route: '/home'};

// Adaptive Skill Paths has no cross-class landing yet — point at the course library for now.
const ALL_CLASSWORK_ITEMS: SidemenuNavItem[] = [
  {icon: 'files', key: 'assignments', label: 'Assignments', route: '/assignments'},
  {icon: 'books', key: 'subjects', label: 'Subjects', route: '/class-subjects'},
  {
    icon: ['fak', 'regular-file-lines-sparkle-bl' as IconName],
    key: 'diagnostics',
    label: 'Adaptive Diagnostics',
    route: '/diagnostics'
  },
  {icon: 'sparkles', key: 'skill-paths', label: 'Adaptive Skill Paths', route: '/subjects'},
  {icon: 'folders', key: 'folders', label: 'Folders', route: '/folders'}
];

interface ClassNavGroups {
  overview: SidemenuNavItem;
  classwork: SidemenuNavItem[];
  manage: SidemenuNavItem[];
}

const getClassNavGroups = (classId: string): ClassNavGroups => ({
  overview: {
    icon: 'chart-pie',
    key: 'overview',
    label: 'Overview',
    route: `/classes/${classId}/students/overview`
  },
  classwork: [
    {
      icon: 'files',
      key: 'assignments',
      label: 'Assignments',
      route: `/classes/${classId}/assignments`
    },
    {icon: 'books', key: 'subjects', label: 'Subjects', route: `/classes/${classId}/subjects`}
  ],
  manage: [
    {icon: 'users', key: 'people', label: 'People', route: `/classes/${classId}/people`},
    {icon: 'cog', key: 'settings', label: 'Settings', route: `/classes/${classId}/settings/details`}
  ]
});

/** Ordered nav sections for the current scope; `label` drives the expanded section header. */
const getNavSections = (
  classGroups: ClassNavGroups | null,
  allClassworkItems: SidemenuNavItem[]
): {items: SidemenuNavItem[]; label?: string}[] =>
  classGroups
    ? [
        {items: [classGroups.overview]},
        {items: classGroups.classwork, label: 'Classwork'},
        {items: classGroups.manage, label: 'Manage'}
      ]
    : [{items: allClassworkItems, label: 'All Classwork'}];

const getItemClassNames = (isSelected: boolean) =>
  classNames(
    'flex grow items-center gap-x1 rounded-x1 px-x2 py-x1-half no-underline content-primary text-s',
    isSelected ? 'background-brand-subtle' : 'hover:background-brand-subtle'
  );

const ItemIcon = ({icon}: {icon: IconProp}) => (
  <Icon className='text-m content-primary opacity-85' fixedWidth icon={icon} iconStyle='regular' />
);

const selectedKeyForGroup = (items: SidemenuNavItem[], pathname: string): string | null => {
  const matches = items.filter(
    (item) => pathname === item.route || pathname.startsWith(`${item.route}/`)
  );
  return matches.length
    ? matches.reduce((best, item) => (item.route.length > best.route.length ? item : best)).key
    : null;
};

/** A nav group: a bare list when unlabeled (Home, Overview), or a labeled section. */
const NavSection = ({
  items,
  label,
  pathname
}: {
  items: SidemenuNavItem[];
  label?: string;
  pathname: string;
}) => {
  const selectedKey = selectedKeyForGroup(items, pathname);
  const rows = (
    <div className='flex flex-col'>
      {items.map((item) => (
        <Link
          className={getItemClassNames(item.key === selectedKey)}
          key={item.key}
          to={item.route}
        >
          <ItemIcon icon={item.icon} />
          {item.label}
        </Link>
      ))}
    </div>
  );
  if (!label) {
    return rows;
  }
  return (
    <section aria-label={label} className='flex flex-col gap-x1-half'>
      <Text className='pr-x1 pl-x2' color='secondary' size='s'>
        {label}
      </Text>
      {rows}
    </section>
  );
};

/** Icon-only nav group for the collapsed rail (mirrors a NavSection, without labels). */
const CollapsedNavGroup = ({items, pathname}: {items: SidemenuNavItem[]; pathname: string}) => {
  const selectedKey = selectedKeyForGroup(items, pathname);
  return (
    <ul className='m-x0 flex list-none flex-col gap-x0-half p-x0'>
      {items.map((item) => (
        <li className='flex' key={item.key}>
          <WithTooltip as='div' className='flex grow' content={item.label} {...TOOLTIP_PROPS}>
            <Link
              aria-label={item.label}
              className={getItemClassNames(item.key === selectedKey)}
              to={item.route}
            >
              <ItemIcon icon={item.icon} />
            </Link>
          </WithTooltip>
        </li>
      ))}
    </ul>
  );
};

interface Props {
  pathname: string;
}

export const Sidemenu = ({pathname}: Props) => {
  const isSidemenuCollapsed = useTeacherDashboardStore((state) => state.isSidemenuCollapsed);
  const setIsSidemenuCollapsed = useTeacherDashboardStore((state) => state.setIsSidemenuCollapsed);
  const [isNewClassModalOpen, setIsNewClassModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);

  const {fetchArchivedClassrooms, fetchClassrooms} = useTeacherClassroomActions();

  // Read only — the dropdown (ClassSelector) owns the archived toggle; we prefetch on mount so a
  // persisted "showing archived" state has data ready when the dropdown opens.
  const isShowingArchived = useTeacherDashboardStore((state) => state.isShowingArchivedClassrooms);
  const setSelectedClassId = useTeacherDashboardStore((state) => state.setSelectedClassId);

  useEffect(() => {
    fetchClassrooms();
    if (isShowingArchived) {
      fetchArchivedClassrooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember the active class so scope-neutral routes (Home) keep its context instead of snapping the
  // selector back to "All Classes". Explicit cross-class destinations clear it; Home leaves it alone.
  // Syncs a module-level store from the route (not derivable in render): each dashboard route mounts its
  // own Sidemenu, so this value must outlive the component — local state would reset on every navigation.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
  useEffect(() => {
    const routeScope = getNavScope(pathname);
    if (routeScope.kind === 'class') {
      setSelectedClassId(routeScope.classId);
    } else if (pathname !== '/home') {
      setSelectedClassId(null);
    }
  }, [pathname, setSelectedClassId]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  // On Home, fall back to the remembered class so its context (selector + nav) persists — but only if
  // that class is still in the teacher's list. A remembered class can go stale: the delete and
  // remove-self flows redirect to /home (which the effect above intentionally never clears), and a
  // masqueraded identity has a different roster. Trusting a stale id would leave Home showing a generic
  // "Class" label with nav links to a class the teacher can't open, so fall through to "All Classes".
  // Resolved inside the selector to a plain id|null, so this nav shell re-renders only when the
  // resolved class changes — not on every classrooms/archivedClassrooms ref swap.
  const validRememberedClassId = useTeacherDashboardStore((state) => {
    const id = state.selectedClassId;
    if (id == null) {
      return null;
    }
    return state.classrooms.some((classroom) => classroom.getId() === id) ||
      state.archivedClassrooms.some((classroom) => classroom.getId() === id)
      ? id
      : null;
  });
  const scope: NavScope =
    pathname === '/home' && validRememberedClassId
      ? {kind: 'class', classId: validRememberedClassId}
      : getNavScope(pathname);
  const classGroups = scope.kind === 'class' ? getClassNavGroups(scope.classId) : null;

  // Hide the cross-class adaptive entries when the flag is off — Diagnostics' route just bounces to
  // /assignments, and Skill Paths ships with the same adaptive launch (Lucy, #18320).
  const {isFeatureEnabled: isAdaptiveDiagnosticsEnabled} =
    useIsFeatureFlagEnabled('adaptive_diagnostics');
  const allClassworkItems = isAdaptiveDiagnosticsEnabled
    ? ALL_CLASSWORK_ITEMS
    : ALL_CLASSWORK_ITEMS.filter((item) => !['diagnostics', 'skill-paths'].includes(item.key));

  const modals = (
    <>
      {isNewClassModalOpen && (
        <NewClassroomModal
          handleClose={() => setIsNewClassModalOpen(false)}
          onSubmit={fetchClassrooms}
        />
      )}
      {isNewFolderModalOpen && (
        <NewFolderModal handleClose={() => setIsNewFolderModalOpen(false)} />
      )}
    </>
  );

  if (isSidemenuCollapsed) {
    return (
      <nav
        aria-label='Teacher dashboard'
        className='flex h-full w-x9 shrink-0 flex-col border-r border-primary background-primary'
      >
        <div className='flex shrink-0 flex-col items-center gap-x1 border-b border-primary p-x1'>
          <IconButton
            icon='sidebar'
            iconStyle='regular'
            label='Expand sidebar'
            tooltip='Expand sidebar'
            tooltipProps={TOOLTIP_PROPS}
            onClick={() => setIsSidemenuCollapsed(false)}
            size='m'
          />
          <CreateNewMenu
            onNewClass={() => setIsNewClassModalOpen(true)}
            onNewFolder={() => setIsNewFolderModalOpen(true)}
            trigger={(triggerProps) => (
              <IconButton
                {...triggerProps}
                color='primary'
                icon='plus'
                label='Create New'
                tooltip='Create New'
                tooltipProps={TOOLTIP_PROPS}
                variant='solid'
              />
            )}
          />
        </div>
        <div
          className={classNames(
            'flex grow flex-col gap-x3 overflow-y-auto px-x1 pt-x2 pb-x4',
            HIDDEN_SCROLLBAR_CLASSES
          )}
        >
          <CollapsedNavGroup items={[HOME_ITEM]} pathname={pathname} />
          <ClassSelector collapsed pathname={pathname} scope={scope} />
          {getNavSections(classGroups, allClassworkItems).map((section) => (
            <CollapsedNavGroup
              items={section.items}
              key={section.label ?? section.items[0].key}
              pathname={pathname}
            />
          ))}
        </div>
        {modals}
      </nav>
    );
  }

  return (
    <nav
      aria-label='Teacher dashboard'
      className='flex h-full w-[280px] shrink-0 flex-col border-r border-primary background-primary'
    >
      <div className='flex shrink-0 items-center justify-between gap-x1 border-b border-primary p-x2'>
        <CreateNewMenu
          onNewClass={() => setIsNewClassModalOpen(true)}
          onNewFolder={() => setIsNewFolderModalOpen(true)}
          trigger={(triggerProps) => (
            <Button {...triggerProps} roundness='pill'>
              Create New
              <Icon className='ml-x1' icon='caret-down' />
            </Button>
          )}
        />
        <IconButton
          icon='sidebar'
          iconStyle='regular'
          label='Collapse sidebar'
          tooltip='Collapse sidebar'
          tooltipProps={TOOLTIP_PROPS}
          onClick={() => setIsSidemenuCollapsed(true)}
          size='m'
        />
      </div>
      <div className='flex grow flex-col gap-x3 overflow-y-auto px-x2 pt-x2 pb-x4'>
        <NavSection items={[HOME_ITEM]} pathname={pathname} />
        <ClassSelector pathname={pathname} scope={scope} />
        {getNavSections(classGroups, allClassworkItems).map((section) => (
          <NavSection
            items={section.items}
            key={section.label ?? section.items[0].key}
            label={section.label}
            pathname={pathname}
          />
        ))}
      </div>
      {modals}
    </nav>
  );
};
